import { createContext, useContext, useRef, useMemo, type ReactNode } from "react";
import type { Token, MapNote } from "../types";
import type {
  LinkRegion,
  LocationLink,
  MapObject,
  MapPin,
  TextObject,
  TokenAction,
} from "@roll1/shared";
import type { Stroke, FogReveal } from "../types";

/**
 * Typed interface for all canvas interaction events.
 * Each Manager registers its handlers, CanvasArea reads from registry.
 */
export interface CanvasCallbacks {
  // Token domain (Spec 3)
  "token:move-end": (tokenId: string, x: number, y: number, prevX: number, prevY: number) => void;
  "token:batch-move-end": (moves: {
    tokenId: string; x: number; y: number;
    prevX: number; prevY: number; tokenName: string;
  }[]) => void;
  "token:double-click": (token: Token) => void;
  "token:context-menu": (token: Token, x: number, y: number) => void;
  "token:double-click-empty": (wx: number, wy: number) => void;
  "token:place-tap": (wx: number, wy: number) => void;
  "token:action": (action: TokenAction) => void;

  // Drawing domain (Spec 2)
  "drawing:stroke-end": (stroke: Stroke) => void;
  "drawing:move-end": (strokeIds: string[], dx: number, dy: number, noteIds?: string[]) => void;
  "drawing:delete-end": (strokeIds: string[], deletedStrokes: Stroke[], noteIds?: string[]) => void;

  // Fog domain (Spec 2)
  "fog:reveal-batch": (reveals: FogReveal[]) => void;
  "fog:close-batch": (closings: FogReveal[]) => void;
  "fog:cover-all": () => void;
  "fog:clear-all": () => void;
  "fog:set-mode": (mode: 'manual' | 'dynamic') => void;

  // Text domain (Spec 1)
  "text:tool-click": (wx: number, wy: number) => void;
  "text:double-click": (wx: number, wy: number) => void;
  "text:get-at-world": (wx: number, wy: number) => TextObject | null;
  "text:delete-selected": (textId: string) => void;
  "text:selected-id": () => string | null;
  "text:open-overlay": (wx: number, wy: number) => void;

  // Location Link domain (Spec 1)
  "link:drawn": (region: LinkRegion) => void;
  "link:click": (link: LocationLink) => void;
  "link:open-edit-dialog": (link: LocationLink) => void;

  // MapObject domain (Spec 1)
  "map-object:drawn": (region: LinkRegion) => void;
  "map-object:select": (objectId: string) => void;
  "map-object:double-click": (obj: MapObject) => void;
  "map-object:move-end": (objectId: string, region: LinkRegion) => void;
  "map-object:delete": (objectId: string) => void;
  "map-object:hover": (obj: MapObject | null, screenX: number, screenY: number) => void;
  "map-object:click-pin": (obj: MapObject | null, screenX: number, screenY: number) => void;

  // Note domain
  "note:tool-click": (wx: number, wy: number) => void;
  "note:click": (note: MapNote) => void;
  "note:move-end": (noteId: string, x: number, y: number) => void;
  "note:quick-create": () => void;
  "note:view": (note: MapNote) => void;
  "note:edit": (note: MapNote) => void;

  // Token domain (Spec 3 additions)
  "token:open-sheet": (sheetId: string) => void;
  "token:paste": () => Token | null;

  // Wall domain
  "wall:tool-click": (wx: number, wy: number) => void;
  "wall:tool-mousemove": (wx: number, wy: number) => void;
  "wall:finish-chain": () => void;
  "wall:cancel-chain": () => void;

  // Ping
  "ping:add": (x: number, y: number, by: string, color?: string) => void;

  // Canvas context menu
  "canvas:context-menu": (screenX: number, screenY: number, worldX: number, worldY: number) => void;

  // Pin domain
  "pin:quick-create": () => void;
  "pin:edit": (pin: MapPin) => void;
  "pin:create-at": (wx: number, wy: number, journalEntryId?: string) => void;

  // Drag & Drop (Spec 3)
  "drop:monster": (slug: string, wx: number, wy: number) => void;
  "drop:sheet-on-token": (sheetId: string, sheetName: string, token: Token) => void;
  "drop:sheet-on-canvas": (sheetId: string, sheetName: string, wx: number, wy: number) => void;
  "drop:journal-entry": (entryId: string, entryName: string, wx: number, wy: number) => void;

  // Inpaint domain (Spec 055 Phase 2)
  "inpaint:region-drawn": (region: import("../hooks/useInpainting").MaskRegion) => void;

  // Detail pass domain (Spec 055 Phase 3)
  "detail:region-drawn": (region: { x: number; y: number; width: number; height: number }) => void;
}

/**
 * Ref-based registry for canvas interaction handlers.
 * Zero re-renders by design.
 */
export interface CanvasInteractionRegistry {
  register<K extends keyof CanvasCallbacks>(
    event: K,
    handler: CanvasCallbacks[K],
  ): () => void;

  get<K extends keyof CanvasCallbacks>(
    event: K,
  ): CanvasCallbacks[K] | undefined;
}

const CanvasInteractionContext = createContext<CanvasInteractionRegistry | null>(null);

export function CanvasInteractionProvider({ children }: { children: ReactNode }) {
  const handlersRef = useRef<
    Partial<Record<keyof CanvasCallbacks, { handler: CanvasCallbacks[keyof CanvasCallbacks]; id: number }>>
  >({});
  const nextIdRef = useRef(0);

  const registry = useMemo<CanvasInteractionRegistry>(() => ({
    register(event, handler) {
      const id = ++nextIdRef.current;
      handlersRef.current[event] = { handler: handler as CanvasCallbacks[keyof CanvasCallbacks], id };
      return () => {
        const entry = handlersRef.current[event];
        if (entry && entry.id === id) {
          delete handlersRef.current[event];
        }
      };
    },
    get(event) {
      const entry = handlersRef.current[event];
      return entry?.handler as any;
    },
  }), []);

  return (
    <CanvasInteractionContext.Provider value={registry}>
      {children}
    </CanvasInteractionContext.Provider>
  );
}

export function useCanvasInteraction(): CanvasInteractionRegistry {
  const ctx = useContext(CanvasInteractionContext);
  if (!ctx) {
    throw new Error("useCanvasInteraction must be used within a CanvasInteractionProvider");
  }
  return ctx;
}
